import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon';

// ── Constantes ────────────────────────────────────────────────────────────────

const PERIODS = [
  { label: '7 dias',  value: '7' },
  { label: '30 dias', value: '30' },
  { label: '90 dias', value: '90' },
  { label: '1 ano',   value: '365' },
  { label: 'Tudo',    value: null },
];

const CATEGORIES = [
  { value: null,       label: 'Todas',      icon: '⭐' },
  { value: 'tour',     label: 'Passeio',    icon: '🏔️' },
  { value: 'app',      label: 'App',        icon: '📱' },
  { value: 'service',  label: 'Atendimento',icon: '💬' },
  { value: 'other',    label: 'Outro',      icon: '✏️' },
];

const EMOJIS       = { 1: '😠', 2: '😕', 3: '😐', 4: '🙂', 5: '😄' };
const EMOJI_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#84cc16', 5: '#22c55e' };

// ── Componente principal ──────────────────────────────────────────────────────

const SatisfactionIndex = (props) => {
  const [days,     setDays]     = useState('30');
  const [category, setCategory] = useState(null);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => { props.setShowMenu(false); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (days)     params.days     = days;
    if (category) params.category = category;

    apiClient.httpInstance
      .get('/app-ratings/dashboard', { params })
      .then(res => { if (res.status === 200) setData(res.data); })
      .finally(() => setLoading(false));
  }, [days, category]);

  return (
    <div className="px-7 pb-10 max-w-5xl">
      {/* ── Cabeçalho ── */}
      <div className="mt-2 mb-5">
        <h1 className="text-2xl font-bold text-white">
          😄 Satisfação de Clientes
        </h1>
        <p className="text-sm mt-1" style={{ color: '#64748b' }}>
          Avaliações enviadas pelos clientes através do FT App.
        </p>
      </div>

      {/* ── Filtros período ── */}
      <div className="flex flex-wrap gap-2 mb-3">
        {PERIODS.map(p => (
          <FilterBtn
            key={p.value ?? 'all'}
            active={days === p.value}
            onClick={() => setDays(p.value)}
            color={props.theme.color}
          >
            {p.label}
          </FilterBtn>
        ))}
      </div>

      {/* ── Filtros categoria ── */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map(c => (
          <FilterBtn
            key={c.value ?? 'all'}
            active={category === c.value}
            onClick={() => setCategory(c.value)}
            color={props.theme.color}
          >
            {c.icon} {c.label}
          </FilterBtn>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <CircularProgress style={{ color: props.theme.color }} />
        </div>
      ) : !data || data.total === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ── KPIs ── */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mb-4">
            <KpiCard
              label="MÉDIA GERAL"
              value={
                <><span style={{ color: avgColor(data.average), fontSize: 28, fontWeight: 700 }}>{data.average?.toFixed(2)}</span>
                  <span style={{ color: '#64748b', fontSize: 14 }}> / 5</span>
                  <span style={{ fontSize: 22, marginLeft: 6 }}>{avgEmoji(data.average)}</span>
                </>
              }
            />
            <KpiCard
              label="% NOTAS BAIXAS (≤2)"
              value={
                <span style={{ color: data.low_pct > 15 ? '#ef4444' : '#22c55e', fontSize: 28, fontWeight: 700 }}>
                  {data.low_pct}%
                </span>
              }
            />
            <KpiCard
              label="TOTAL AVALIAÇÕES"
              value={<span style={{ color: '#e2e8f0', fontSize: 28, fontWeight: 700 }}>{data.total}</span>}
            />
            <KpiCard
              label="ALERTAS"
              value={
                <span style={{ color: data.low_count > 0 ? '#f59e0b' : '#22c55e', fontSize: 28, fontWeight: 700 }}>
                  {data.low_count}
                </span>
              }
            />
          </div>

          {/* ── Distribuição + Tendência ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
            <Section title="📊 Distribuição de notas">
              <DistBar distribution={data.distribution} total={data.total} />
            </Section>
            <Section title="📈 Tendência semanal (12 semanas)">
              <TrendChart weeks={data.week_trend} themeColor={props.theme.color} />
            </Section>
          </div>

          {/* ── Por categoria ── */}
          {data.by_category?.length > 0 && (
            <Section title="🗂️ Por categoria" className="mb-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {data.by_category.map((c, i) => (
                  <div key={i} className="p-4 rounded-xl" style={{ background: '#0a1929', border: '1px solid #1e3a5f' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ fontSize: 20 }}>{c.category_icon}</span>
                      <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>{c.category_label}</span>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: avgColor(c.average) }}>
                      {c.average?.toFixed(2)}
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#475569' }}>{c.total} avaliações</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Por região ── */}
          {data.by_region?.length > 0 && (
            <Section title="🌍 Por região" className="mb-4">
              <div className="divide-y" style={{ borderColor: '#1e3a5f' }}>
                {data.by_region.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-white">{r.region_name}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{r.total} avaliações</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold" style={{ color: avgColor(r.average) }}>
                        {r.average?.toFixed(2)}
                      </span>
                      <span style={{ fontSize: 22 }}>{avgEmoji(r.average)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Alertas — notas baixas ── */}
          <Section
            title={`⚠️ Notas baixas que precisam de atenção (${data.low_count})`}
            className="mb-4"
            titleColor={data.low_count > 0 ? '#f59e0b' : undefined}
          >
            {data.low_ratings?.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: '#64748b' }}>
                ✨ Nenhuma nota baixa neste período. Continue assim!
              </p>
            ) : (
              <div className="space-y-3">
                {data.low_ratings.map(r => <LowCard key={r.id} r={r} />)}
              </div>
            )}
          </Section>

          {/* ── Avaliações recentes ── */}
          <Section title="🕐 Avaliações recentes">
            <div className="divide-y" style={{ borderColor: '#1e3a5f' }}>
              {data.recent?.map(r => <RecentRow key={r.id} r={r} />)}
            </div>
          </Section>

          {!data.enough_data && (
            <p className="text-xs mt-4 text-center" style={{ color: '#475569' }}>
              ℹ️ Dados estatisticamente relevantes a partir de 10 avaliações. Você tem {data.total}.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ── Sub-componentes ───────────────────────────────────────────────────────────

const FilterBtn = ({ active, onClick, color, children }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-lg text-sm transition-all"
    style={{
      background: active ? color + '33' : '#0f2340',
      border: `1px solid ${active ? color : '#1e3a5f'}`,
      color: active ? '#fff' : '#94a3b8',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

const Section = ({ title, children, className = '', titleColor }) => (
  <div className={`p-5 rounded-xl ${className}`} style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}>
    <h2 className="text-sm font-semibold mb-4" style={{ color: titleColor || '#94a3b8' }}>{title}</h2>
    {children}
  </div>
);

const KpiCard = ({ label, value }) => (
  <div className="p-4 rounded-xl" style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}>
    <p className="text-xs tracking-wider mb-2" style={{ color: '#64748b' }}>{label}</p>
    <div className="flex items-center flex-wrap gap-1">{value}</div>
  </div>
);

const DistBar = ({ distribution, total }) => {
  const max = Math.max(...Object.values(distribution), 1);
  return (
    <div className="flex items-end justify-around gap-2" style={{ height: 120 }}>
      {[1,2,3,4,5].map(v => {
        const count = distribution[v] || 0;
        const pct   = Math.round((count / max) * 100);
        return (
          <div key={v} className="flex flex-col items-center gap-1 flex-1">
            <span className="text-xs" style={{ color: '#94a3b8' }}>{count}</span>
            <div
              className="w-full rounded-t-lg transition-all"
              style={{ height: `${Math.max(pct, 4)}%`, background: count > 0 ? EMOJI_COLORS[v] : '#1e3a5f', minHeight: 6 }}
            />
            <span style={{ fontSize: 18 }}>{EMOJIS[v]}</span>
          </div>
        );
      })}
    </div>
  );
};

const TrendChart = ({ weeks, themeColor }) => {
  if (!weeks || weeks.length < 2) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm italic text-center" style={{ color: '#475569' }}>
          Precisamos de pelo menos 2 semanas para mostrar a tendência.
        </p>
      </div>
    );
  }
  const W = 300; const H = 100; const padX = 10; const padY = 10;
  const points = weeks.map((w, i) => ({
    x: padX + (i / (weeks.length - 1)) * (W - padX * 2),
    y: H - padY - ((w.average - 1) / 4) * (H - padY * 2),
    ...w,
  }));
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 200 }}>
        {[1,2,3,4,5].map(v => {
          const y = H - padY - ((v - 1) / 4) * (H - padY * 2);
          return <line key={v} x1={padX} y1={y} x2={W - padX} y2={y} stroke="#1e3a5f" strokeWidth="0.5" strokeDasharray="3,3" />;
        })}
        <path d={path} fill="none" stroke={themeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}><circle cx={p.x} cy={p.y} r={3.5} fill={themeColor} />
            <title>{p.week}: {p.average} ({p.total} aval.)</title>
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-xs mt-1" style={{ color: '#475569' }}>
        <span>{weeks[0]?.week}</span><span>{weeks[weeks.length - 1]?.week}</span>
      </div>
    </div>
  );
};

const LowCard = ({ r }) => (
  <div className="p-3 rounded-lg" style={{ background: '#1a070733', border: '1px solid #7f1d1d55' }}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: 14 }}>{r.category_icon}</span>
          <span className="text-sm font-medium text-white">{r.category_label}</span>
          {r.reference_name && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#1e3a5f', color: '#94a3b8' }}>
              {r.reference_name}
            </span>
          )}
        </div>
        <p className="text-xs mt-1" style={{ color: '#64748b' }}>
          {r.customer_name} · {r.created_at}
        </p>
        {r.comment && (
          <p className="text-xs mt-2 italic" style={{ color: '#94a3b8' }}>"{r.comment}"</p>
        )}
      </div>
      <span style={{ fontSize: 26, flexShrink: 0 }}>{r.rating_emoji}</span>
    </div>
  </div>
);

const RecentRow = ({ r }) => (
  <div className="flex items-center justify-between py-3 gap-3">
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <span style={{ fontSize: 18, flexShrink: 0 }}>{r.category_icon}</span>
      <div className="min-w-0">
        <p className="text-sm text-white truncate">
          {r.customer_name}
          {r.reference_name && <span style={{ color: '#64748b' }}> · {r.reference_name}</span>}
        </p>
        <p className="text-xs mt-0.5" style={{ color: '#475569' }}>{r.category_label} · {r.created_at}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <span style={{ fontSize: 22 }}>{r.rating_emoji}</span>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-24 rounded-xl" style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}>
    <span style={{ fontSize: 48 }}>📱</span>
    <p className="mt-3 text-base font-medium text-white">Nenhuma avaliação ainda</p>
    <p className="mt-1 text-sm text-center max-w-xs" style={{ color: '#64748b' }}>
      As avaliações aparecerão automaticamente assim que os clientes começarem a usar o FT App.
    </p>
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const avgColor = (avg) => {
  if (!avg) return '#94a3b8';
  if (avg >= 4) return '#22c55e';
  if (avg >= 3) return '#eab308';
  return '#ef4444';
};

const avgEmoji = (avg) => {
  if (!avg) return '😐';
  if (avg >= 4.5) return '😄';
  if (avg >= 3.5) return '🙂';
  if (avg >= 2.5) return '😐';
  if (avg >= 1.5) return '😕';
  return '😠';
};

const mapStateToProps = state => ({ user: state.user });
export default connect(mapStateToProps)(SatisfactionIndex);
